interface SerializeParamsOptions {
    onError?: (error: Error) => void;
    type: string;
}

/**
 * Max length allowed for custom names taken from:
 * https://github.com/microsoft/ApplicationInsights-JS/blob/ae71ebf806da82e149b0af60f00d70b2c1d3e3f0/legacy/JavaScript/JavaScriptSDK/Telemetry/Common/DataSanitizer.ts#L15
 */
const maxPropertyKeyNameLength = 150;

/**
 * Max length allowed for custom values taken from:
 * https://github.com/microsoft/ApplicationInsights-JS/blob/ae71ebf806da82e149b0af60f00d70b2c1d3e3f0/legacy/JavaScript/JavaScriptSDK/Telemetry/Common/DataSanitizer.ts#L25
 */
const maxPropertyStringLength = 8192;

/**
 * Max length allowed for messages taken from:
 * https://github.com/microsoft/ApplicationInsights-JS/blob/ae71ebf806da82e149b0af60f00d70b2c1d3e3f0/legacy/JavaScript/JavaScriptSDK/Telemetry/Common/DataSanitizer.ts#L40.
 */
const maxMessageStringLength = 32768;

const keyPadSuffixLength = 3;

const sanitizePropertyKey = (key: string, finalProps: Record<string, string>) => {
    let finalKey = key;

    finalKey = sanitizeString(finalKey, maxPropertyKeyNameLength);
    let i = 0;
    const maxKeyPadNumber = 10 ** keyPadSuffixLength;

    // Make sure the sanitized key will be unique
    while (finalProps[finalKey] !== undefined) {
        if (i >= maxKeyPadNumber) {
            throw Error(`Can not serialize key: ${key}. Due to MAX_NAME_LENGTH: ${maxPropertyKeyNameLength}`);
        }
        finalKey =
            finalKey.substring(0, maxPropertyKeyNameLength - keyPadSuffixLength) +
            i.toString(10).padStart(keyPadSuffixLength, '0');
        i++;
    }

    return finalKey;
};

const sanitizeString = (string: string, maxlength: number) => string.substring(0, maxlength);

const serializeParamValue = (value: unknown) => {
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'number') {
        return value.toString(10);
    }
    if (typeof value === 'undefined') {
        return 'undefined';
    }
    if (value === null) {
        return 'null';
    }
    if (typeof value === 'function' && typeof value.toString === 'function') {
        return value.toString();
    }

    return JSON.stringify(value) || 'unknown';
};

export const serializeParams = (params: Record<string, unknown>, { onError, type }: SerializeParamsOptions) => {
    const serializedParams: Record<string, string> = {};

    Object.keys(params).forEach((key) => {
        try {
            const finalKey = sanitizePropertyKey(key, serializedParams);
            const value = params[key];
            const jsonParamValue = serializeParamValue(value);
            serializedParams[finalKey] = sanitizeString(jsonParamValue, maxPropertyStringLength);
        } catch (e) {
            if (onError) {
                onError(
                    new Error(
                        `Can't serialize key: ${key}, to JSON. This key will be missing in the Azure application logs. Original error message: ${e.message}. Type: ${type}`
                    )
                );
            }
        }
    });

    return serializedParams;
};

export const serializeMessage = (message: string) => sanitizeString(message, maxMessageStringLength);
